import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  TextField,
  Stack,
  Switch,
  Alert,
  Avatar,
  Badge,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import firebase from "firebase/compat/app";
import { ExpandMore as ExpandMoreIcon } from "../icons";
import { BugReport as BugReportIcon } from "../icons";
import { Flag as FlagIcon } from "../icons";
import { SupportAgent as SupportAgentIcon } from "../icons";
import { Forum as ForumIcon } from "../icons";
import { ArrowBack as ArrowBackIcon } from "../icons";
import { Send as SendIcon } from "../icons";
import { AccountBalance as AccountBalanceIcon } from "../icons";
import { Link as RouterLink } from "react-router-dom";
import { db, functions } from "../firebase";
import { brandColors } from "../theme";
import { T } from "./Translate";

const dateFmt = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "short",
  timeStyle: "short",
});

const toDate = (ts) => (ts && typeof ts.toDate === "function" ? ts.toDate() : null);
const formatTs = (ts) => {
  const d = toDate(ts);
  return d ? dateFmt.format(d) : "—";
};

// --- Barra de errores por día (14 días), un solo color: la altura codifica la magnitud. ---
const DailyBarChart = ({ docs }) => {
  const [hovered, setHovered] = useState(null);

  const days = useMemo(() => {
    const buckets = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 13; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      buckets.push({ date: d, count: 0 });
    }
    docs.forEach((doc) => {
      const d = toDate(doc.createdAt);
      if (!d) return;
      const dayStart = new Date(d);
      dayStart.setHours(0, 0, 0, 0);
      const bucket = buckets.find((b) => b.date.getTime() === dayStart.getTime());
      if (bucket) bucket.count += 1;
    });
    return buckets;
  }, [docs]);

  const max = Math.max(1, ...days.map((d) => d.count));
  const chartHeight = 120;
  const slotWidth = 24;
  const barWidth = 14;

  return (
    <Box sx={{ position: "relative", overflowX: "auto" }}>
      <svg
        width={days.length * slotWidth}
        height={chartHeight + 24}
        role="img"
        aria-label="Errores reportados por día, últimos 14 días"
      >
        {days.map((d, i) => {
          const barHeight = Math.round((d.count / max) * (chartHeight - 8));
          const x = i * slotWidth + (slotWidth - barWidth) / 2;
          const y = chartHeight - barHeight;
          const isHovered = hovered === i;
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(barHeight, 2)}
                rx={4}
                fill={isHovered ? brandColors.accent : "#5d72c9"}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
                style={{ cursor: "default", transition: "fill 0.1s" }}
              />
              {i % 2 === 0 && (
                <text
                  x={x + barWidth / 2}
                  y={chartHeight + 16}
                  fontSize="9"
                  textAnchor="middle"
                  fill={brandColors.textSecondary}
                >
                  {d.date.getDate()}/{d.date.getMonth() + 1}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hovered !== null && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: Math.min(hovered * slotWidth, days.length * slotWidth - 90),
            bgcolor: brandColors.backgroundPaper,
            border: `1px solid ${brandColors.accent}`,
            borderRadius: 1,
            px: 1,
            py: 0.5,
            pointerEvents: "none",
            fontSize: 12,
            whiteSpace: "nowrap",
          }}
        >
          {dateFmt.format(days[hovered].date).split(",")[0]}: <b>{days[hovered].count}</b>
        </Box>
      )}
    </Box>
  );
};

const StatTile = ({ icon, label, value, accentColor }) => (
  <Card sx={{ flex: "1 1 180px", minWidth: 160 }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ color: accentColor || brandColors.accent }}>{icon}</Box>
      <Box>
        <Typography variant="h5" sx={{ lineHeight: 1 }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
          {label}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const groupCrashes = (docs) => {
  const map = new Map();
  docs.forEach((doc) => {
    const key = doc.message || "(sin mensaje)";
    if (!map.has(key)) {
      map.set(key, {
        message: key,
        stack: doc.stack,
        count: 0,
        fatalCount: 0,
        resolvedCount: 0,
        lastSeen: doc.createdAt,
        ids: [],
        contexts: [],
      });
    }
    const g = map.get(key);
    g.count += 1;
    if (doc.fatal) g.fatalCount += 1;
    if (doc.resolved) g.resolvedCount += 1;
    const dLast = toDate(g.lastSeen);
    const dCur = toDate(doc.createdAt);
    if (!dLast || (dCur && dCur > dLast)) g.lastSeen = doc.createdAt;
    g.ids.push(doc.id);
    if (doc.context && g.contexts.length < 3) g.contexts.push(doc.context);
  });
  return Array.from(map.values()).sort((a, b) => {
    const da = toDate(a.lastSeen)?.getTime() || 0;
    const db_ = toDate(b.lastSeen)?.getTime() || 0;
    return db_ - da;
  });
};

const CrashReportsTab = ({ docs, loading }) => {
  const [showResolved, setShowResolved] = useState(false);
  const groups = useMemo(() => groupCrashes(docs), [docs]);
  const visible = groups.filter((g) => showResolved || g.resolvedCount < g.count);

  const markResolved = async (group) => {
    const batch = db.batch();
    group.ids.forEach((id) => {
      batch.update(db.collection("crashReports").doc(id), { resolved: true });
    });
    await batch.commit();
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box>
      <Button size="small" onClick={() => setShowResolved((v) => !v)} sx={{ mb: 1 }}>
        {showResolved ? <T>Ocultar resueltos</T> : <T>Ver también resueltos</T>}
      </Button>
      {visible.length === 0 && (
        <Typography sx={{ color: brandColors.textSecondary }}>
          <T>Sin errores pendientes. 🎉</T>
        </Typography>
      )}
      {visible.map((g) => {
        const isResolved = g.resolvedCount >= g.count;
        return (
          <Accordion key={g.message} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", width: "100%" }}>
                <Chip
                  size="small"
                  label={g.fatalCount > 0 ? "fatal" : "no fatal"}
                  color={g.fatalCount > 0 ? "error" : "warning"}
                />
                {isResolved && <Chip size="small" label="resuelto" color="success" />}
                <Typography sx={{ flex: 1, fontFamily: "monospace", fontSize: 13 }}>
                  {g.message}
                </Typography>
                <Chip size="small" label={`×${g.count}`} />
                <Typography variant="caption" sx={{ color: brandColors.textSecondary }}>
                  {formatTs(g.lastSeen)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {g.stack && (
                <Box
                  component="pre"
                  sx={{
                    fontSize: 11,
                    whiteSpace: "pre-wrap",
                    bgcolor: "#161616",
                    color: "#e0e0e0",
                    p: 1,
                    borderRadius: 1,
                    maxHeight: 240,
                    overflow: "auto",
                  }}
                >
                  {g.stack}
                </Box>
              )}
              {!isResolved && (
                <Button size="small" variant="outlined" onClick={() => markResolved(g)} sx={{ mt: 1 }}>
                  <T>Marcar como resuelto</T>
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

// Ruta pública de lo reportado (ver reportItem en homeUtils.js y los
// targetType que usan home/events/Hilos/ProfilePage).
const REPORT_TARGET_PATHS = {
  offer: "/offers",
  event: "/events",
  thread: "/hilos",
  car: "/cars",
  place: "/trips",
  profile: "/profile",
};

const ReportsTab = ({ docs, loading }) => {
  const [showReviewed, setShowReviewed] = useState(false);
  const visible = docs.filter((d) => showReviewed || d.status !== "reviewed");

  const markReviewed = async (id) => {
    await db.collection("reports").doc(id).update({ status: "reviewed" });
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box>
      <Button size="small" onClick={() => setShowReviewed((v) => !v)} sx={{ mb: 1 }}>
        {showReviewed ? <T>Ocultar revisados</T> : <T>Ver también revisados</T>}
      </Button>
      {visible.length === 0 && (
        <Typography sx={{ color: brandColors.textSecondary }}>
          <T>No hay reportes pendientes.</T>
        </Typography>
      )}
      {visible.map((r) => (
        <Card key={r.id} sx={{ mb: 1 }}>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Chip size="small" label={r.targetType || "?"} />
            {r.status === "reviewed" && <Chip size="small" label="revisado" color="success" />}
            {REPORT_TARGET_PATHS[r.targetType] ? (
              <Button
                size="small"
                component={RouterLink}
                to={`${REPORT_TARGET_PATHS[r.targetType]}/${r.targetId}`}
                target="_blank"
                sx={{ textTransform: "none" }}
              >
                <T>Ver lo reportado</T>
              </Button>
            ) : (
              <Typography sx={{ fontFamily: "monospace", fontSize: 12 }}>
                target: {r.targetId}
              </Typography>
            )}
            {r.companyId && r.targetType !== "profile" && (
              <Button
                size="small"
                component={RouterLink}
                to={`/profile/${r.companyId}`}
                target="_blank"
                sx={{ textTransform: "none" }}
              >
                <T>Ver autor</T>
              </Button>
            )}
            <Typography variant="caption" sx={{ color: brandColors.textSecondary }}>
              reportado por {r.reporterId} · {formatTs(r.createdAt)}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {r.status !== "reviewed" && (
              <Button size="small" variant="outlined" onClick={() => markReviewed(r.id)}>
                <T>Marcar revisado</T>
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

// Chat de soporte con una persona (el usuario escribe desde SupportChat.js).
// Bandeja a la izquierda, conversación a la derecha; en el celular, una u
// otra. El resumen supportChats/{uid} lo mantiene onSupportChatMessage
// (functions): acá solo se marca leído/resuelto y se responde.
const SupportChatConversation = ({ chat, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [surveys, setSurveys] = useState({});
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const endRef = useRef(null);
  const chatRef = db.collection("supportChats").doc(chat.uid);

  // Borrador de respuesta con la IA (functions/aiAssistant.js); se revisa
  // antes de mandarlo.
  const suggest = async () => {
    setSuggesting(true);
    try {
      const res = await functions.httpsCallable("aiSuggestSupportReply", { timeout: 300000 })({ uid: chat.uid });
      if (res.data?.suggestion) setText(res.data.suggestion);
    } catch (error) {
      console.error("Error al sugerir respuesta:", error);
    } finally {
      setSuggesting(false);
    }
  };

  useEffect(() => {
    const unsub = db
      .collection("supportChats")
      .doc(chat.uid)
      .collection("messages")
      .orderBy("createdAt", "asc")
      .onSnapshot(
        (snap) => {
          setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        (error) => {
          console.error("Error al leer el chat de soporte:", error);
          setLoading(false);
        }
      );
    return unsub;
  }, [chat.uid]);

  useEffect(() => {
    return db
      .collection("supportChats")
      .doc(chat.uid)
      .collection("surveys")
      .onSnapshot(
        (snap) => setSurveys(Object.fromEntries(snap.docs.map((d) => [d.id, d.data()]))),
        (error) => console.error("Error al leer las encuestas de soporte:", error)
      );
  }, [chat.uid]);

  // Abrir el chat lo marca leído (también cuando llega un mensaje nuevo con
  // el chat abierto).
  useEffect(() => {
    if (chat.unreadByAdmin) {
      db.collection("supportChats").doc(chat.uid).update({ unreadByAdmin: false }).catch(() => {});
    }
  }, [chat.uid, chat.unreadByAdmin]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const clean = text.trim().slice(0, 2000);
    if (!clean || sending) return;
    setSending(true);
    try {
      await chatRef.collection("messages").add({
        from: "admin",
        text: clean,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      setText("");
    } catch (error) {
      console.error("Error al responder el chat de soporte:", error);
    } finally {
      setSending(false);
    }
  };

  // Al resolverlo vuelve a modo IA: si el usuario escribe de nuevo, contesta
  // primero el asistente. También deja el aviso "system", que al usuario le
  // muestra que el chat finalizó y la encuesta (SupportChat.js).
  const toggleResolved = () => {
    const batch = db.batch();
    if (chat.status === "closed") {
      batch.update(chatRef, { status: "open", unreadByAdmin: false });
    } else {
      batch.update(chatRef, { status: "closed", unreadByAdmin: false, mode: "ai" });
      batch.set(chatRef.collection("messages").doc(), {
        from: "system",
        text: "El chat finalizó.",
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    }
    return batch.commit().catch((error) => console.error("Error al cambiar el estado del chat:", error));
  };

  return (
    <Card sx={{ display: "flex", flexDirection: "column", height: { xs: "70vh", md: 560 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <IconButton onClick={onBack} sx={{ display: { md: "none" } }} aria-label="volver">
          <ArrowBackIcon />
        </IconButton>
        <Avatar src={chat.profileImageUrl} sx={{ width: 32, height: 32 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component={RouterLink}
            to={`/profile/${chat.uid}`}
            noWrap
            sx={{ display: "block", fontWeight: 600, color: "text.primary", textDecoration: "none" }}
          >
            {chat.name || chat.uid}
          </Typography>
          <Typography variant="caption" sx={{ color: brandColors.textSecondary }}>
            {chat.mode === "human" ? "Atiende una persona" : "Atiende la IA"}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={toggleResolved} sx={{ flexShrink: 0 }}>
          {chat.status === "closed" ? <T>Reabrir</T> : <T>Marcar resuelto</T>}
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
        {loading && <CircularProgress size={24} />}
        {messages.map((m) => {
          if (m.from === "system") {
            const survey = surveys[m.id];
            return (
              <Box key={m.id} sx={{ textAlign: "center", my: 2 }}>
                <Typography variant="caption" sx={{ display: "block", color: brandColors.textSecondary, fontWeight: 600 }}>
                  <T>Chat finalizado</T> · {formatTs(m.createdAt)}
                </Typography>
                {survey ? (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} aria-label={`${survey.rating} de 5`}>
                      {"★".repeat(survey.rating)}{"☆".repeat(5 - survey.rating)} {survey.rating}/5
                    </Typography>
                    {survey.comment && (
                      <Typography variant="body2" sx={{ color: brandColors.textSecondary, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {survey.comment}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="caption" sx={{ display: "block", color: brandColors.textSecondary }}>
                    <T>Encuesta sin responder</T>
                  </Typography>
                )}
              </Box>
            );
          }
          const mine = m.from !== "user";
          return (
            <Box key={m.id} sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", mb: 1 }}>
              <Box
                sx={{
                  maxWidth: "80%",
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: m.from === "admin" ? brandColors.accent : "action.selected",
                  color: m.from === "admin" ? (brandColors.mode === "light" ? "#ffffff" : "#0a0a1a") : "text.primary",
                  ...(m.from === "ai" && { border: 1, borderColor: "divider", borderStyle: "dashed" }),
                }}
              >
                {m.from === "ai" && (
                  <Typography variant="caption" sx={{ display: "block", fontWeight: 700, opacity: 0.8 }}>
                    IA
                  </Typography>
                )}
                {/* color "inherit": el tema le fija el color a todo Typography y
                    sin esto ignora el de la burbuja. */}
                <Typography variant="body2" sx={{ color: "inherit", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {m.text}
                </Typography>
                <Typography variant="caption" sx={{ color: "inherit", display: "block", textAlign: "right", opacity: 0.7 }}>
                  {formatTs(m.createdAt)}
                </Typography>
              </Box>
            </Box>
          );
        })}
        {chat.aiTyping && (
          <Typography variant="caption" sx={{ color: brandColors.textSecondary, fontStyle: "italic" }}>
            La IA está escribiendo...
          </Typography>
        )}
        <div ref={endRef} />
      </Box>

      <Box
        component="form"
        onSubmit={send}
        sx={{ display: "flex", alignItems: "flex-end", gap: 1, p: 1.5, borderTop: 1, borderColor: "divider" }}
      >
        <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) send(e);
          }}
          placeholder="Responder..."
          size="small"
          fullWidth
          multiline
          maxRows={4}
          inputProps={{ maxLength: 2000 }}
        />
        <Button size="small" onClick={suggest} disabled={suggesting} sx={{ flexShrink: 0, textTransform: "none" }}>
          {suggesting ? <CircularProgress size={16} /> : <T>Sugerir con IA</T>}
        </Button>
        <IconButton type="submit" disabled={!text.trim() || sending} aria-label="Enviar" sx={{ color: brandColors.accent }}>
          <SendIcon />
        </IconButton>
      </Box>
    </Card>
  );
};

const SupportChatsTab = ({ chats, loading }) => {
  const [showResolved, setShowResolved] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);
  const visible = chats.filter((c) => showResolved || c.status !== "closed");
  const selected = chats.find((c) => c.uid === selectedUid) || null;

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
      <Box sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0, display: { xs: selected ? "none" : "block", md: "block" } }}>
        <Button size="small" onClick={() => setShowResolved((v) => !v)} sx={{ mb: 1 }}>
          {showResolved ? <T>Ocultar resueltos</T> : <T>Ver también resueltos</T>}
        </Button>
        {visible.length === 0 && (
          <Typography sx={{ color: brandColors.textSecondary }}>
            <T>No hay chats de soporte abiertos.</T>
          </Typography>
        )}
        <List disablePadding>
          {visible.map((c) => (
            <ListItemButton
              key={c.uid}
              selected={c.uid === selectedUid}
              onClick={() => setSelectedUid(c.uid)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemAvatar>
                <Badge color="error" variant="dot" invisible={!c.unreadByAdmin}>
                  <Avatar src={c.profileImageUrl} />
                </Badge>
              </ListItemAvatar>
              <ListItemText
                disableTypography
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontWeight: c.unreadByAdmin ? 700 : 500, flex: 1, minWidth: 0 }} noWrap>
                      {c.name || c.uid}
                    </Typography>
                    {c.status === "closed" ? (
                      <Chip size="small" label="resuelto" color="success" />
                    ) : (
                      <Chip size="small" label={c.mode === "human" ? "persona" : "IA"} variant="outlined" />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ color: brandColors.textSecondary }}>
                    <Typography variant="body2" noWrap>
                      {c.lastFrom === "admin" ? "Vos: " : c.lastFrom === "ai" ? "IA: " : ""}
                      {c.lastMessage}
                    </Typography>
                    <Typography variant="caption">{formatTs(c.lastMessageAt)}</Typography>
                  </Box>
                }
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: { xs: selected ? "block" : "none", md: "block" } }}>
        {selected ? (
          <SupportChatConversation key={selected.uid} chat={selected} onBack={() => setSelectedUid(null)} />
        ) : (
          <Typography sx={{ color: brandColors.textSecondary, mt: 5, textAlign: "center" }}>
            <T>Elegí un chat para responder.</T>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const SupportTab = ({ docs, loading }) => {
  const [showHandled, setShowHandled] = useState(false);
  const visible = docs.filter((d) => showHandled || !d.handled);

  const markHandled = async (id) => {
    await db.collection("supportMessages").doc(id).update({ handled: true });
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box>
      <Button size="small" onClick={() => setShowHandled((v) => !v)} sx={{ mb: 1 }}>
        {showHandled ? <T>Ocultar atendidos</T> : <T>Ver también atendidos</T>}
      </Button>
      {visible.length === 0 && (
        <Typography sx={{ color: brandColors.textSecondary }}>
          <T>No hay mensajes de soporte pendientes.</T>
        </Typography>
      )}
      {visible.map((m) => (
        <Card key={m.id} sx={{ mb: 1 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              {m.handled && <Chip size="small" label="atendido" color="success" />}
              {m.type === "account-recovery" && <Chip size="small" label="recuperación de cuenta" color="warning" />}
              <Typography sx={{ fontWeight: 600 }}>{m.name || m.email}</Typography>
              <Typography variant="caption" sx={{ color: brandColors.textSecondary }}>
                {m.email} · {formatTs(m.createdAt)}
              </Typography>
            </Box>
            <Typography sx={{ mt: 1, whiteSpace: "pre-wrap" }}>{m.message}</Typography>
            {!m.handled && (
              <Button size="small" variant="outlined" onClick={() => markHandled(m.id)} sx={{ mt: 1 }}>
                <T>Marcar atendido</T>
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

// Cuentas oficiales de gobiernos/organismos públicos: se otorgan a mano
// buscando al usuario por email (ver officialAccounts en firestore.rules,
// solo el admin puede escribir esta colección).
const GovernmentAccountsTab = ({ docs, loading }) => {
  const [email, setEmail] = useState("");
  const [granting, setGranting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleGrant = async (event) => {
    event.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) return;

    setGranting(true);
    setFeedback(null);
    try {
      // Por función: el email ya no está en users/{uid} (privacidad de
      // contacto), así que se busca en Firebase Auth del lado del servidor.
      await functions.httpsCallable("grantOfficialAccount")({ email: cleanEmail });
      setFeedback({ type: "success", message: <T>Cuenta oficial otorgada.</T> });
      setEmail("");
    } catch (error) {
      console.error("Error al otorgar cuenta oficial:", error);
      setFeedback({
        type: "error",
        message: error.code === "functions/not-found"
          ? <T>No existe ningún usuario con ese email.</T>
          : <T>No pudimos otorgar la cuenta oficial. Inténtalo más tarde.</T>,
      });
    } finally {
      setGranting(false);
    }
  };

  const toggleEnabled = async (docId, enabled) => {
    await db.collection("officialAccounts").doc(docId).update({ enabled: !enabled });
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            <T>Otorgar cuenta oficial</T>
          </Typography>
          <Box component="form" onSubmit={handleGrant}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                size="small"
                fullWidth
                label={<T>Email del usuario</T>}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={granting}
              />
              <Button type="submit" variant="contained" disabled={granting || !email.trim()}>
                {granting ? <CircularProgress size={20} color="inherit" /> : <T>Otorgar</T>}
              </Button>
            </Stack>
          </Box>
          {feedback && (
            <Alert severity={feedback.type} sx={{ mt: 2 }}>
              {feedback.message}
            </Alert>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <CircularProgress size={24} />
      ) : docs.length === 0 ? (
        <Typography sx={{ color: brandColors.textSecondary }}>
          <T>Todavía no hay cuentas oficiales otorgadas.</T>
        </Typography>
      ) : (
        docs.map((d) => (
          <Card key={d.id} sx={{ mb: 1 }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <AccountBalanceIcon sx={{ color: brandColors.accent }} />
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Typography sx={{ fontWeight: 600 }}>{d.displayName || d.email}</Typography>
                <Typography variant="caption" sx={{ color: brandColors.textSecondary }}>
                  {d.email} · <T>otorgada</T> {formatTs(d.grantedAt)}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={d.enabled ? <T>habilitada</T> : <T>deshabilitada</T>}
                color={d.enabled ? "success" : "default"}
              />
              <Switch checked={Boolean(d.enabled)} onChange={() => toggleEnabled(d.id, d.enabled)} />
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );
};

const AdminPanel = () => {
  const [tab, setTab] = useState(0);
  const [crashes, setCrashes] = useState([]);
  const [reports, setReports] = useState([]);
  const [supportMsgs, setSupportMsgs] = useState([]);
  const [officialAccounts, setOfficialAccounts] = useState([]);
  const [supportChats, setSupportChats] = useState([]);
  const [loading, setLoading] = useState({ crashes: true, reports: true, support: true, official: true, chats: true });

  useEffect(() => {
    const onError = (key) => (error) => {
      console.error(`Error al leer ${key}:`, error);
      setLoading((s) => ({ ...s, [key]: false }));
    };

    const unsubCrashes = db
      .collection("crashReports")
      .orderBy("createdAt", "desc")
      .limit(300)
      .onSnapshot((snap) => {
        setCrashes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading((s) => ({ ...s, crashes: false }));
      }, onError("crashes"));

    const unsubReports = db
      .collection("reports")
      .orderBy("createdAt", "desc")
      .limit(200)
      .onSnapshot((snap) => {
        setReports(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading((s) => ({ ...s, reports: false }));
      }, onError("reports"));

    const unsubSupport = db
      .collection("supportMessages")
      .orderBy("createdAt", "desc")
      .limit(200)
      .onSnapshot((snap) => {
        setSupportMsgs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading((s) => ({ ...s, support: false }));
      }, onError("support"));

    const unsubOfficial = db
      .collection("officialAccounts")
      .orderBy("grantedAt", "desc")
      .onSnapshot((snap) => {
        setOfficialAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading((s) => ({ ...s, official: false }));
      }, onError("official"));

    const unsubChats = db
      .collection("supportChats")
      .orderBy("lastMessageAt", "desc")
      .limit(200)
      .onSnapshot((snap) => {
        setSupportChats(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading((s) => ({ ...s, chats: false }));
      }, onError("chats"));

    return () => {
      unsubChats();
      unsubCrashes();
      unsubReports();
      unsubSupport();
      unsubOfficial();
    };
  }, []);

  const unresolvedCrashGroups = useMemo(
    () => groupCrashes(crashes).filter((g) => g.resolvedCount < g.count).length,
    [crashes]
  );
  const pendingReports = reports.filter((r) => r.status !== "reviewed").length;
  const pendingSupport = supportMsgs.filter((m) => !m.handled).length;
  const unreadChats = supportChats.filter((c) => c.unreadByAdmin).length;

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        <T>Panel Admin</T>
      </Typography>
      <Typography variant="body2" sx={{ color: brandColors.textSecondary, mb: 3 }}>
        <T>Errores, reportes y soporte en un solo lugar.</T>
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <StatTile
          icon={<BugReportIcon />}
          label="Grupos de errores sin resolver"
          value={unresolvedCrashGroups}
          accentColor="#ff6b6b"
        />
        <StatTile
          icon={<FlagIcon />}
          label="Reportes pendientes"
          value={pendingReports}
          accentColor="#ffb84d"
        />
        <StatTile
          icon={<SupportAgentIcon />}
          label="Mensajes de soporte sin atender"
          value={pendingSupport}
          accentColor={brandColors.accent}
        />
        <StatTile
          icon={<ForumIcon />}
          label="Chats sin responder"
          value={unreadChats}
          accentColor="#27ae60"
        />
      </Box>


      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            <T>Errores reportados · últimos 14 días</T>
          </Typography>
          {loading.crashes ? <CircularProgress size={24} /> : <DailyBarChart docs={crashes} />}
        </CardContent>
      </Card>

      {/* En el celular las pestañas no entran: se corren con las flechas. */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 2,
          // palette.primary casi no se ve sobre los fondos oscuros.
          "& .MuiTab-root.Mui-selected": { color: brandColors.accent },
          "& .MuiTabs-indicator": { bgcolor: brandColors.accent },
        }}
      >
        <Tab label={<T>Crashes</T>} />
        <Tab label={<T>Reportes</T>} />
        <Tab label={<T>Soporte</T>} />
        <Tab
          label={
            <Badge color="error" badgeContent={unreadChats} sx={{ "& .MuiBadge-badge": { right: -10 } }}>
              <T>Chats</T>
            </Badge>
          }
        />
        <Tab label={<T>Gobiernos</T>} />
      </Tabs>

      {tab === 0 && <CrashReportsTab docs={crashes} loading={loading.crashes} />}
      {tab === 1 && <ReportsTab docs={reports} loading={loading.reports} />}
      {tab === 2 && <SupportTab docs={supportMsgs} loading={loading.support} />}
      {tab === 3 && <SupportChatsTab chats={supportChats} loading={loading.chats} />}
      {tab === 4 && <GovernmentAccountsTab docs={officialAccounts} loading={loading.official} />}
    </Box>
  );
};

export default AdminPanel;
